import React, { useCallback } from 'react'
import {
  View,
  Text,
  Pressable,
  Platform,
  ActivityIndicator,
  Image as RNImage,
  Switch,
} from 'react-native'
import { Image as ExpoImage } from 'expo-image'
import { Download, Trash2, ImagePlus } from 'lucide-react-native'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { useSnackbar } from '../../../shared/hooks/useSnackbar'
import { useDialog } from '../../../shared/hooks/useDialog'
import { useApiClient } from '../../../../helpers/apiClient.helper'
import { useB2bImageUpload } from '../../hooks/useB2bImageUpload'
import { downloadOrgLogoViaApi } from '../../../shared/helpers/downloadOrgLogoViaApi.helper'
import { useAccountAdminCopy } from '../../hooks/useAccountAdminCopy'
import type { B2bBrandingData } from '../../types/b2bAccount.types'
import type { Theme } from '../../../theme/getTheme'
import type { getAccountBrandingTabStyles } from '../AccountBrandingTab/AccountBrandingTab.styles'

interface BrandingLogoSectionProps {
  accountId: string
  branding: B2bBrandingData | null
  onRefresh: () => Promise<void>
  onUseBannerBackgroundChange: (next: boolean) => Promise<void>
  styles: ReturnType<typeof getAccountBrandingTabStyles>
  theme: Theme
}

export const BrandingLogoSection: React.FC<BrandingLogoSectionProps> = ({
  accountId,
  branding,
  onRefresh,
  onUseBannerBackgroundChange,
  styles,
  theme,
}) => {
  const copy = useAccountAdminCopy()
  const { showSnackbar } = useSnackbar()
  const { showDialog } = useDialog()
  const apiClient = useApiClient()
  const remainingLogoSlots = Math.max(
    0,
    copy.B2B_LOGO_MAX_COUNT - (branding?.logoCdnUrls?.length ?? 0),
  )
  const {
    isUploading,
    uploadError,
    setUploadError,
    uploadLogos,
    removeLogoAtIndex,
    removingLogoIndex,
    uploadBanner,
    removeBanner,
  } = useB2bImageUpload(accountId, remainingLogoSlots)

  const hasBanner = Boolean(branding?.bannerCdnUrl)
  const useBannerBackground = Boolean(branding?.useBannerBackground)

  const handleUploadLogos = useCallback(async () => {
    setUploadError(null)
    await uploadLogos()
    await onRefresh()
  }, [uploadLogos, onRefresh, setUploadError])

  const handleRemoveLogo = useCallback(
    async (index: number) => {
      const ok = await showDialog({
        title: copy.REMOVE_LOGO_CONFIRM_TITLE,
        message: copy.REMOVE_LOGO_CONFIRM_MESSAGE,
        type: 'danger',
        confirmText: copy.REMOVE_LOGO,
        cancelText: 'Cancel',
      })
      if (!ok) return
      await removeLogoAtIndex(index)
      await onRefresh()
    },
    [removeLogoAtIndex, onRefresh, showDialog],
  )

  const handleDownloadLogo = useCallback(
    async (logoIndex: number, publicCdnUrl: string) => {
      try {
        await downloadOrgLogoViaApi({ accountId, logoIndex, publicCdnUrl, apiClient })
      } catch {
        showSnackbar(copy.DOWNLOAD_LOGO_ERROR, 'error')
      }
    },
    [accountId, apiClient, showSnackbar],
  )

  const handleUploadBanner = useCallback(async () => {
    setUploadError(null)
    await uploadBanner()
    await onRefresh()
  }, [uploadBanner, onRefresh, setUploadError])

  const handleRemoveBanner = useCallback(async () => {
    const ok = await showDialog({
      title: copy.REMOVE_BANNER_CONFIRM_TITLE,
      message: copy.REMOVE_BANNER_CONFIRM_MESSAGE,
      type: 'danger',
      confirmText: copy.REMOVE_BANNER,
      cancelText: 'Cancel',
    })
    if (!ok) return
    await removeBanner()
    await onRefresh()
  }, [removeBanner, onRefresh, showDialog])

  const handleToggleUseBannerBackground = useCallback(
    async (next: boolean) => {
      try {
        await onUseBannerBackgroundChange(next)
        showSnackbar(copy.USE_BANNER_BACKGROUND_SAVED, 'success')
      } catch {
        /* surface handled upstream */
      }
    },
    [onUseBannerBackgroundChange, showSnackbar],
  )

  const bannerUri = branding?.bannerCdnUrl ?? null
  const logoUri = branding && branding.logoCdnUrls.length > 0 ? branding.logoCdnUrls[0] : null

  return (
    <>
      {isUploading ? (
        <View style={styles.section}>
          <ActivityIndicator color={theme.primary} />
        </View>
      ) : null}
      {uploadError ? <Text style={styles.errorText}>{uploadError}</Text> : null}

      <View style={[styles.section, styles.sectionFlushBottom]}>
        <Text style={styles.sectionTitle}>{copy.BRAND_IMAGERY_SECTION_TITLE}</Text>

        {/* Logo tile */}
        <View style={styles.brandImageryTileBlock}>
          <Text style={styles.brandImageryTileLabel}>{copy.LOGO_TILE_LABEL}</Text>
          <Text style={styles.bodyText}>{copy.LOGOS_UPLOAD_HINT}</Text>
          {logoUri ? (
            <View style={styles.logoPreviewRow}>
              <View style={styles.logoThumbColumn}>
                {Platform.OS === 'web' ? (
                  <RNImage source={{ uri: logoUri }} style={styles.logoThumb} resizeMode="contain" />
                ) : (
                  <ExpoImage source={{ uri: logoUri }} style={styles.logoThumb} contentFit="contain" />
                )}
                <View style={styles.logoActionsRow}>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={copy.DOWNLOAD_LOGO}
                    disabled={isUploading || removingLogoIndex !== null}
                    onPress={() => void handleDownloadLogo(0, logoUri)}
                    style={({ pressed, hovered }) => [
                      styles.downloadLogoButton,
                      Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                      pressed && styles.downloadLogoButtonPressed,
                      hovered && Platform.OS === 'web' && styles.downloadLogoButtonHover,
                      (isUploading || removingLogoIndex !== null) && styles.downloadLogoButtonDisabled,
                    ]}
                  >
                    <Download
                      size={16}
                      color={theme.primary}
                      accessibilityElementsHidden
                      importantForAccessibility="no"
                    />
                    <Text style={styles.downloadLogoButtonText}>{copy.DOWNLOAD_LOGO}</Text>
                  </Pressable>
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={copy.REMOVE_LOGO}
                    disabled={isUploading || removingLogoIndex !== null}
                    onPress={() => void handleRemoveLogo(0)}
                    style={({ pressed, hovered }) => [
                      styles.removeLogoButton,
                      Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                      pressed && styles.removeLogoButtonPressed,
                      hovered && Platform.OS === 'web' && styles.removeLogoButtonHover,
                      (isUploading || removingLogoIndex !== null) && styles.removeLogoButtonDisabled,
                    ]}
                  >
                    <Trash2
                      size={16}
                      color={theme.error}
                      accessibilityElementsHidden
                      importantForAccessibility="no"
                    />
                    <Text style={styles.removeLogoButtonText}>{copy.REMOVE_LOGO}</Text>
                  </Pressable>
                </View>
              </View>
            </View>
          ) : (
            <Pressable
              accessibilityRole="button"
              disabled={isUploading || remainingLogoSlots <= 0}
              onPress={() => void handleUploadLogos()}
              style={({ pressed, hovered }) => [
                styles.brandImageryEmptyTile,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.brandImageryEmptyTilePressed,
                hovered && Platform.OS === 'web' && styles.brandImageryEmptyTileHover,
                (isUploading || remainingLogoSlots <= 0) && styles.brandImageryEmptyTileDisabled,
              ]}
            >
              <ImagePlus
                size={20}
                color={theme.primary}
                accessibilityElementsHidden
                importantForAccessibility="no"
              />
              <Text style={styles.brandImageryEmptyTileText}>{copy.LOGO_TILE_EMPTY}</Text>
            </Pressable>
          )}
        </View>

        {/* Banner tile */}
        <View style={styles.brandImageryTileBlock}>
          <Text style={styles.brandImageryTileLabel}>{copy.BANNER_TILE_LABEL}</Text>
          <Text style={styles.bodyText}>{copy.BANNER_UPLOAD_HINT}</Text>
          {bannerUri ? (
            <View style={styles.bannerPreviewColumn}>
              {Platform.OS === 'web' ? (
                <RNImage source={{ uri: bannerUri }} style={styles.bannerThumb} resizeMode="cover" />
              ) : (
                <ExpoImage source={{ uri: bannerUri }} style={styles.bannerThumb} contentFit="cover" />
              )}
              <View style={styles.logoActionsRow}>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={copy.REPLACE_BANNER}
                  disabled={isUploading}
                  onPress={() => void handleUploadBanner()}
                  style={({ pressed, hovered }) => [
                    styles.downloadLogoButton,
                    Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                    pressed && styles.downloadLogoButtonPressed,
                    hovered && Platform.OS === 'web' && styles.downloadLogoButtonHover,
                    isUploading && styles.downloadLogoButtonDisabled,
                  ]}
                >
                  <ImagePlus
                    size={16}
                    color={theme.primary}
                    accessibilityElementsHidden
                    importantForAccessibility="no"
                  />
                  <Text style={styles.downloadLogoButtonText}>{copy.REPLACE_BANNER}</Text>
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  accessibilityLabel={copy.REMOVE_BANNER}
                  disabled={isUploading}
                  onPress={() => void handleRemoveBanner()}
                  style={({ pressed, hovered }) => [
                    styles.removeLogoButton,
                    Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                    pressed && styles.removeLogoButtonPressed,
                    hovered && Platform.OS === 'web' && styles.removeLogoButtonHover,
                    isUploading && styles.removeLogoButtonDisabled,
                  ]}
                >
                  <Trash2
                    size={16}
                    color={theme.error}
                    accessibilityElementsHidden
                    importantForAccessibility="no"
                  />
                  <Text style={styles.removeLogoButtonText}>{copy.REMOVE_BANNER}</Text>
                </Pressable>
              </View>
            </View>
          ) : (
            <Pressable
              accessibilityRole="button"
              disabled={isUploading}
              onPress={() => void handleUploadBanner()}
              style={({ pressed, hovered }) => [
                styles.brandImageryEmptyTileWide,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.brandImageryEmptyTilePressed,
                hovered && Platform.OS === 'web' && styles.brandImageryEmptyTileHover,
                isUploading && styles.brandImageryEmptyTileDisabled,
              ]}
            >
              <ImagePlus
                size={20}
                color={theme.primary}
                accessibilityElementsHidden
                importantForAccessibility="no"
              />
              <Text style={styles.brandImageryEmptyTileText}>{copy.BANNER_TILE_EMPTY}</Text>
            </Pressable>
          )}
        </View>

        {/* Toggle: use banner image as squad background */}
        <View style={styles.useBannerToggleRow}>
          <View style={styles.useBannerToggleText}>
            <Text style={styles.useBannerToggleLabel}>
              {copy.USE_BANNER_BACKGROUND_LABEL}
            </Text>
            <Text style={styles.useBannerToggleHint}>
              {hasBanner
                ? copy.USE_BANNER_BACKGROUND_HINT
                : copy.USE_BANNER_BACKGROUND_DISABLED_HINT}
            </Text>
          </View>
          <Switch
            value={useBannerBackground && hasBanner}
            disabled={!hasBanner || isUploading}
            onValueChange={(next) => void handleToggleUseBannerBackground(next)}
            trackColor={{ false: theme.gray[300], true: theme.primary }}
            thumbColor={theme.white}
          />
        </View>
      </View>
    </>
  )
}
